import type { AxiosInstance, AxiosResponse } from 'axios';
import type {
    BbaasListResponse,
    BbaasBgpSessionsResponse,
    BbaasRoutingTableResponse,
    FetchBackboneListParams,
    FetchBbaasBgpSessionsParams,
    FetchBbaasRoutingTableParams,
    UpdateBbaasBody,
    UpdateBbaasResponse,
    UpdateVrfDisplayNameResponse,
} from './bbaas.types';
import type {
    BbConnectorListResponse,
    BbConnectorPerformanceResponse,
    BbConnectorBgpResponse,
    BbConnectorRoutesResponse,
    FetchBbConnectorListParams,
    FetchBbConnectorPerformanceParams,
} from './bbConnector.types';

/**
 * Fetch BBAAS (backbone) list for the given tenant.
 * GET /kontroledge/bbaas?tenantId=&limit=10&offset=0&search=&includeChildServices=true
 */
export function fetchBackboneList(
    api: AxiosInstance,
    tenantId: number,
    params: FetchBackboneListParams = {}
): Promise<AxiosResponse<BbaasListResponse>> {
    const { limit = 10, offset = 0, search } = params;
    const query = new URLSearchParams({
        tenantId: String(tenantId),
        limit: String(limit),
        offset: String(offset),
        includeChildServices: 'true',
    });
    if (search != null && search.trim() !== '') {
        query.set('search', search.trim());
    }
    return api.get<BbaasListResponse>(`/kontroledge/bbaas?${query.toString()}`);
}

/**
 * PUT /kontroledge/bbaas/:id?tenantId=
 * Update BBAAS name and/or tags.
 */
export function updateBbaas(
    api: AxiosInstance,
    tenantId: number,
    bbaasId: number,
    body: UpdateBbaasBody
): Promise<AxiosResponse<UpdateBbaasResponse>> {
    return api.put<UpdateBbaasResponse>(
        `/kontroledge/bbaas/${bbaasId}?tenantId=${tenantId}`,
        body
    );
}

/**
 * PUT /kontroledge/bbaas/vrf/:vrfId?tenantId=
 * Update user-facing VRF displayName (internal `name` is not editable).
 */
export function updateVrfDisplayName(
    api: AxiosInstance,
    tenantId: number,
    vrfId: number,
    displayName: string
): Promise<AxiosResponse<UpdateVrfDisplayNameResponse>> {
    return api.put<UpdateVrfDisplayNameResponse>(
        `/kontroledge/bbaas/vrf/${vrfId}?tenantId=${tenantId}`,
        {
            displayName: displayName.trim(),
        }
    );
}

/**
 * Fetch BB-Connector list.
 * GET /kontroledge/bbaas/bb-connector?tenantId=&limit=10&offset=0&search=
 */
export function fetchBbConnectorList(
    api: AxiosInstance,
    tenantId: number,
    params: FetchBbConnectorListParams = {}
): Promise<AxiosResponse<BbConnectorListResponse>> {
    const { limit = 10, offset = 0, search } = params;
    const query = new URLSearchParams({
        tenantId: String(tenantId),
        limit: String(limit),
        offset: String(offset),
    });
    if (search != null && search.trim() !== '') {
        query.set('search', search.trim());
    }
    return api.get<BbConnectorListResponse>(
        `/kontroledge/bbaas/bb-connector?${query.toString()}`
    );
}

/**
 * GET /kontroledge/bbaas/bb-connector/:connectorId/performance?tenantId=&timeRange=&interval=&maxPoints=
 */
export function fetchBbConnectorPerformance(
    api: AxiosInstance,
    tenantId: number,
    connectorId: number,
    params: FetchBbConnectorPerformanceParams = {}
): Promise<AxiosResponse<BbConnectorPerformanceResponse>> {
    const query = new URLSearchParams({ tenantId: String(tenantId) });
    const { timeRange, interval, maxPoints, start, end } = params;
    if (timeRange != null && timeRange !== '') query.set('timeRange', timeRange);
    if (interval != null && interval !== '') query.set('interval', interval);
    if (maxPoints != null) query.set('maxPoints', String(maxPoints));
    if (start != null && start !== '') query.set('start', start);
    if (end != null && end !== '') query.set('end', end);
    return api.get<BbConnectorPerformanceResponse>(
        `/kontroledge/bbaas/bb-connector/${connectorId}/performance?${query.toString()}`
    );
}

/**
 * GET /kontroledge/bbaas/bb-connector/:connectorId/bgp?tenantId=
 */
export function fetchBbConnectorBgp(
    api: AxiosInstance,
    tenantId: number,
    connectorId: number
): Promise<AxiosResponse<BbConnectorBgpResponse>> {
    return api.get<BbConnectorBgpResponse>(
        `/kontroledge/bbaas/bb-connector/${connectorId}/bgp?tenantId=${tenantId}`
    );
}

/**
 * GET /kontroledge/bbaas/bb-connector/:connectorId/routes?tenantId=
 */
export function fetchBbConnectorRoutes(
    api: AxiosInstance,
    tenantId: number,
    connectorId: number
): Promise<AxiosResponse<BbConnectorRoutesResponse>> {
    return api.get<BbConnectorRoutesResponse>(
        `/kontroledge/bbaas/bb-connector/${connectorId}/routes?tenantId=${tenantId}`
    );
}

/**
 * GET /kontroledge/bbaas/:bbaasId/bgp-sessions?tenantId=&limit=&offset=
 */
export function fetchBbaasBgpSessions(
    api: AxiosInstance,
    tenantId: number,
    bbaasId: number,
    params: FetchBbaasBgpSessionsParams = {}
): Promise<AxiosResponse<BbaasBgpSessionsResponse>> {
    const { limit = 100, offset = 0 } = params;
    const query = new URLSearchParams({
        tenantId: String(tenantId),
        limit: String(limit),
        offset: String(offset),
    });
    return api.get<BbaasBgpSessionsResponse>(
        `/kontroledge/bbaas/${bbaasId}/bgp-sessions?${query.toString()}`
    );
}

/**
 * GET /kontroledge/bbaas/:bbaasId/routing-table?tenantId=&limit=&offset= [&vrfId=]
 */
export function fetchBbaasRoutingTable(
    api: AxiosInstance,
    tenantId: number,
    bbaasId: number,
    params: FetchBbaasRoutingTableParams
): Promise<AxiosResponse<BbaasRoutingTableResponse>> {
    const { vrfId, limit = 10, offset = 0 } = params;
    const query = new URLSearchParams({
        tenantId: String(tenantId),
        limit: String(limit),
        offset: String(offset),
    });
    if (vrfId != null) {
        query.set('vrfId', String(vrfId));
    }
    return api.get<BbaasRoutingTableResponse>(
        `/kontroledge/bbaas/${bbaasId}/routing-table?${query.toString()}`
    );
}

/* ── API → UI mappers (same import path as fetchers) ── */
export {
    bbConnectorServiceSubscriptionId,
    connectorSearchHaystack,
    formatBbConnectorConnectedServiceTypeLabel,
    formatBbConnectorServiceDetail,
    mapBbaasBgpSessionToConnector,
    mapBbaasItemToBackboneService,
    mapBbConnectorItemToTopologyConnector,
    mapConnectedServiceTypeToConnectorType,
    parseBbaasTags,
    sanitizeTopologyFileName,
} from './mappers';

/* ── Re-export API types (single import path: `./requests/request`) ── */
export type {
    BbaasBgpSessionItem,
    BbaasBgpSessionVrf,
    BbaasBgpSessionsPagination,
    BbaasBgpSessionsResponse,
    BbaasListItem,
    BbaasListPagination,
    BbaasListResponse,
    BbaasRoutingTablePagination,
    BbaasRoutingTableResponse,
    BbaasRoutingTableRow,
    BbaasVrfItem,
    FetchBackboneListParams,
    FetchBbaasBgpSessionsParams,
    FetchBbaasRoutingTableParams,
    UpdateBbaasBody,
    UpdateBbaasResponse,
    UpdateBbaasResponseData,
    UpdateVrfDisplayNameResponse,
    UpdateVrfDisplayNameResponseData,
} from './bbaas.types';

export type {
    BbConnectorBandwidth,
    BbConnectorBandwidthUtilization,
    BbConnectorBgpResponse,
    BbConnectorBgpResponseData,
    BbConnectorBgpSession,
    BbConnectorBgpStatus,
    BbConnectorHostConfig,
    BbConnectorListItem,
    BbConnectorListPagination,
    BbConnectorListResponse,
    BbConnectorLocation,
    BbConnectorPerformanceData,
    BbConnectorPerformancePoint,
    BbConnectorPerformanceResponse,
    BbConnectorRouteItem,
    BbConnectorRoutesResponse,
    BbConnectorRoutesResponseData,
    BbConnectorRoutesVrf,
    BbConnectorServiceDetail,
    BbConnectorVrf,
    FetchBbConnectorListParams,
    FetchBbConnectorPerformanceParams,
} from './bbConnector.types';
